import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import ThemeToggle from "@/components/ThemeToggle";
import TextSizeToggle from "@/components/TextSizeToggle";
import {
  LayoutDashboard, Calendar as CalIcon, Ship, BookOpen, Fish, MapPin,
  Briefcase, Wrench, FileText, BarChart3, Settings, LogOut, Anchor, Menu, X, Map as MapIcon, Receipt, ShieldCheck, Sparkles, Trash, Cloud, BookMarked, AlertTriangle,
} from "lucide-react";

const ADMIN_ROLES = new Set(["super_admin", "owner_director", "operations_admin"]);

const NAV = [
  { to: "/app/dashboard", key: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/calendar", key: "nav.calendar", icon: CalIcon, testid: "nav-calendar" },
  { to: "/app/assets", key: "nav.assets", icon: Ship, testid: "nav-assets" },
  { to: "/app/journal", key: "nav.journal", icon: BookOpen, testid: "nav-journal" },
  { to: "/app/damage", key: "nav.damage", icon: AlertTriangle, testid: "nav-damage" },
  { to: "/app/fishing", key: "nav.fishing", icon: Fish, testid: "nav-fishing" },
  { to: "/app/map", key: "nav.map", icon: MapIcon, testid: "nav-map" },
  { to: "/app/weather", key: "nav.weather", icon: Cloud, testid: "nav-weather" },
  { to: "/app/places", key: "nav.places", icon: MapPin, testid: "nav-places" },
  { to: "/app/commercial", key: "nav.commercial", icon: Briefcase, testid: "nav-commercial" },
  { to: "/app/maintenance", key: "nav.maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/app/invoices", key: "nav.invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/app/documents", key: "nav.documents", icon: FileText, testid: "nav-documents" },
  { to: "/app/reports", key: "nav.reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/app/intelligence", key: "nav.intelligence", icon: Sparkles, testid: "nav-intelligence" },
  { to: "/app/trash", key: "nav.trash", icon: Trash, testid: "nav-trash" },
  { to: "/app/manual", key: "nav.manual", icon: BookMarked, testid: "nav-manual" },
  { to: "/app/approvals", key: "nav.approvals", icon: ShieldCheck, testid: "nav-approvals", adminOnly: true },
  { to: "/app/settings", key: "nav.settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const visibleNav = NAV.filter(n => !n.adminOnly || ADMIN_ROLES.has(user?.role));
  const activeNav = visibleNav.find(n => location.pathname.startsWith(n.to));
  const currentTitle = activeNav ? t(activeNav.key) : "LaSovita Ledger";

  return (
    <div className="min-h-screen flex bg-[#050A14] text-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 shrink-0 bg-[#070D1B] border-r border-[#1E293B] z-40 transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2 px-5 py-5 border-b border-[#1E293B]" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1.25rem)" }}>
          <div className="w-9 h-9 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/30 flex items-center justify-center">
            <Anchor className="w-5 h-5 text-[#4DA6FF]" />
          </div>
          <div>
            <div className="font-display text-base font-bold tracking-tight">LASOVITA</div>
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">LEDGER · v1.0</div>
          </div>
          <button className="lg:hidden ml-auto text-[#94A3B8]" onClick={() => setMobileOpen(false)} data-testid="sidebar-close-mobile">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="sidebar-nav px-3 py-4 overflow-y-auto h-[calc(100vh-210px)]" data-testid="sidebar-nav">
          {visibleNav.map(({ to, key, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm mb-1 transition-colors ${
                  isActive
                    ? "bg-[#007AFF]/15 text-[#F8FAFC] border border-[#007AFF]/30"
                    : "text-[#94A3B8] hover:bg-[#131C31] hover:text-[#F8FAFC] border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" /> <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-[#1E293B] p-3 space-y-2" data-testid="sidebar-user-footer">
          <TextSizeToggle />
          <ThemeToggle />
          <LanguageToggle />
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-md bg-[#131C31] border border-[#1E293B] flex items-center justify-center font-display text-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="font-mono text-[10px] text-[#94A3B8] truncate">{user?.role ? t(`role.${user.role}`) : "—"}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-md hover:bg-[#131C31] text-[#94A3B8] hover:text-[#F87171] transition-colors"
              title={t("common.signOut")}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="glass-header sticky top-0 z-20" data-testid="app-header">
          <div className="flex items-center justify-between px-5 lg:px-8 h-16">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden text-[#94A3B8]"
                onClick={() => setMobileOpen(true)}
                data-testid="sidebar-open-mobile"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="font-display text-xl font-bold tracking-tight" data-testid="page-title">{currentTitle}</h1>
              <span className="hidden sm:inline font-mono text-[10px] tracking-widest text-[#94A3B8]">
                · {t("header.subtitle")}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#94A3B8]">
              <span className="font-mono" data-testid="header-clock">{new Date().toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</span>
              <Link to="/app/dashboard" className="hidden sm:inline hover:text-[#F8FAFC]">{t("header.home")}</Link>
            </div>
          </div>
        </header>
        <div className="px-5 lg:px-8 py-6 lg:py-8 animate-fade-up">
          {children}
        </div>
      </main>
    </div>
  );
}
